import animation from '../animation';
import configs from '../configs';
import ComputeRenderer from '../gpgpu/compute-renderer';
import {blendRGB} from '../helpers/blend-color';
import {colorToRGBArray, lerp} from '../helpers/helpers';
import {hasDarkBackground} from '../helpers/helpers-mapbox';
import MeshSet from '../mesh-set';
import {CAR_GAP_RATIO, CAR_MESH_LENGTH_KM} from '../mesh-set/car-geometry';
import {Point} from 'maplibre-gl';
import {getCameraMercatorZ} from '../helpers/maplibre-compat';
import {Color, Scene, WebGLRenderTarget, Vector3} from 'three';

// 오버레이 색이 들어가는 재사용 슬롯. 한 번에 한 객체만 덮으므로 하나면 된다 —
// 새로 칠할 때마다 색 그룹을 만들면 freeIndices 가 계속 재활용되며 텍스처가 흔들린다.
const TINT_COLOR_ID = 'tint.overlay';

const MAX_UG_CARS = 1000;
const MAX_OG_CARS = 2500;
const MAX_AIRCRAFTS = 200;
const MAX_BUSES = 4000;

export default class {

    constructor(options) {
        const me = this;

        me.id = options.id;
        me.type = 'three';
        me.lightColor = 'white';
        me.objects = new Map();

        me.onCameraChanged = me.onCameraChanged.bind(me);
    }

    onAdd(map, context) {
        const me = this,
            scene = context.scene,
            modelOrigin = map.getModelOrigin(),
            chunkSize = context.renderer.capabilities.maxTextureSize,
            meshSetParams = {
                zoom: map.getZoom(),
                cameraZ: getCameraMercatorZ(map.map),
                modelScale: map.getModelScale()
            };

        me.map = map;
        me.context = context;

        me.computeRenderer = new ComputeRenderer(MAX_UG_CARS + MAX_OG_CARS + MAX_AIRCRAFTS + MAX_BUSES, {modelOrigin, chunkSize});

        const ugCarMeshSet = me.ugCarMeshSet = new MeshSet('CAR', MAX_UG_CARS, meshSetParams),
            ogCarMeshSet = me.ogCarMeshSet = new MeshSet('CAR', MAX_OG_CARS, meshSetParams),
            aircraftMeshSet = me.aircraftMeshSet = new MeshSet('AIRCRAFT', MAX_AIRCRAFTS, meshSetParams),
            busMeshSet = me.busMeshSet = new MeshSet('BUS', MAX_BUSES, meshSetParams);

        scene.add(ugCarMeshSet.getMesh());
        scene.add(ogCarMeshSet.getMesh());
        scene.add(aircraftMeshSet.getMesh());
        scene.add(busMeshSet.getMesh());

        scene.add(ugCarMeshSet.getDelayMarkerMesh());
        scene.add(ogCarMeshSet.getDelayMarkerMesh());

        scene.add(ugCarMeshSet.getOutlineMesh());
        scene.add(ogCarMeshSet.getOutlineMesh());
        scene.add(aircraftMeshSet.getOutlineMesh());
        scene.add(busMeshSet.getOutlineMesh());

        const ugPickingScene = me.ugPickingScene = new Scene();

        ugPickingScene.background = new Color(0xFFFFFF);
        ugPickingScene.add(ugCarMeshSet.getPickingMesh());

        const ogPickingScene = me.ogPickingScene = new Scene();

        ogPickingScene.background = new Color(0xFFFFFF);
        ogPickingScene.add(ogCarMeshSet.getPickingMesh());
        ogPickingScene.add(aircraftMeshSet.getPickingMesh());
        ogPickingScene.add(busMeshSet.getPickingMesh());

        me.pickingTexture = new WebGLRenderTarget(1, 1);
        me.pixelBuffer = new Uint8Array(4);

        me.animationID = animation.start({
            callback: () => {
                me.needsUpdateInstances = true;
            },
            frameRate: 1
        });

        map.on('zoom', me.onCameraChanged);
        map.on('pitch', me.onCameraChanged);
    }

    onRemove(map) {
        const me = this;

        me.computeRenderer.dispose();

        me.ugCarMeshSet.dispose();
        me.ogCarMeshSet.dispose();
        me.aircraftMeshSet.dispose();
        me.busMeshSet.dispose();

        me.pickingTexture.dispose();

        animation.stop(me.animationID);

        map.off('zoom', me.onCameraChanged);
        map.off('pitch', me.onCameraChanged);
    }

    prerender(map, context) {
        const me = this,
            textures = me.computeRenderer.compute(context, map.layerZoom);

        me.ugCarMeshSet.setTextures(textures);
        me.ogCarMeshSet.setTextures(textures);
        me.aircraftMeshSet.setTextures(textures);
        me.busMeshSet.setTextures(textures);

        if (me.needsUpdateInstances) {
            // Reassign object instances into mesh sets based on type and altitude
            const [ugCarInstanceIDs, ogCarInstanceIDs, aircraftInstanceIDs, busInstanceIDs] = me.computeRenderer.getInstanceIDs(context);

            me.ugCarMeshSet.setInstanceIDs(ugCarInstanceIDs);
            me.ogCarMeshSet.setInstanceIDs(ogCarInstanceIDs);
            me.aircraftMeshSet.setInstanceIDs(aircraftInstanceIDs);
            me.busMeshSet.setInstanceIDs(busInstanceIDs);
            delete me.needsUpdateInstances;
        }
    }

    refreshCars() {
        const me = this,
            instanceIDs = me.carInstanceIDs;

        if (!instanceIDs) {
            return;
        }

        // Only the gap changes with the zoom, so nudge the cars instead of rebuilding
        // them - rebuilding restarts their fade-in and makes the train flicker.
        const spacing = me.getCarSpacing();

        for (let i = 0, ilen = instanceIDs.length; i < ilen; i++) {
            me.computeRenderer.setInstanceCarOffset(instanceIDs[i], spacing * (i + 1));
        }
    }

    onCameraChanged() {
        const me = this,
            map = me.map,
            cameraParams = {
                zoom: map.getZoom(),
                cameraZ: getCameraMercatorZ(map.map)
            };

        me.ugCarMeshSet.refreshCameraParams(cameraParams);
        me.ogCarMeshSet.refreshCameraParams(cameraParams);
        me.aircraftMeshSet.refreshCameraParams(cameraParams);
        me.busMeshSet.refreshCameraParams(cameraParams);
        me.refreshCars();
    }

    setMode(viewMode, searchMode) {
        const me = this,
            currentOpacity = me.computeRenderer.getOpacity();
        let ugOpacity, ogOpacity;

        if (searchMode !== 'none' && searchMode !== 'edit') {
            ugOpacity = ogOpacity = .1;
        } else if (viewMode === 'underground') {
            ugOpacity = .9;
            ogOpacity = .225;
        } else if (viewMode === 'both') {
            // 지하 열차도 지상과 같은 진하기로 달린다.
            ugOpacity = .85;
            ogOpacity = .9;
        } else {
            ugOpacity = .225;
            ogOpacity = .9;
        }
        animation.start({
            callback: (elapsed, duration) => {
                me.computeRenderer.setOpacity({
                    ground: lerp(currentOpacity.ground, ogOpacity, elapsed / duration),
                    underground: lerp(currentOpacity.underground, ugOpacity, elapsed / duration),
                });
                me.refreshDelayMarkers();
            },
            duration: configs.transitionDuration
        });
    }

    addRouteGroup(data) {
        return this.computeRenderer.addRouteGroup(data);
    }

    removeRouteGroup(groupIndex) {
        this.computeRenderer.removeRouteGroup(groupIndex);
    }

    addColorGroup(data) {
        return this.computeRenderer.addColorGroup(data);
    }

    removeColorGroup(groupIndex) {
        this.computeRenderer.removeColorGroup(groupIndex);
    }

    /**
     * Colour index for a bus: its route's colour, or the shade for its occupancy when
     * the feed reports one. A bus with no reported occupancy keeps the route colour,
     * so "the feed does not cover this bus" stays different from "this bus is empty".
     *
     * Falls back to the feed-wide colour when the id is not registered, which happens
     * when a GTFS lists a trip whose route_id is missing from routes.txt. getColorIndex
     * throws on a miss and a throw here stops every object from being drawn.
     * @param {Object} object - Bus
     * @param {boolean} [base] - True to ignore occupancy and take the route colour
     * @returns {number} Colour index
     */
    getBusColorIndex(object, base) {
        const me = this,
            {computeRenderer} = me,
            {gtfsId, trip, occupancy} = object,
            routeId = `${gtfsId}.${trip.route}`;

        if (!base && occupancy !== undefined && computeRenderer.hasColor(`${routeId}.${occupancy}`)) {
            return computeRenderer.getColorIndex(`${routeId}.${occupancy}`);
        }
        return computeRenderer.getColorIndex(computeRenderer.hasColor(routeId) ? routeId : gtfsId);
    }

    addObject(object) {
        const me = this;
        let objectType, routeIndex, colorIndex, baseColorIndex, sectionIndex, nextSectionIndex, delay;

        if (object.type === 'train') {
            const {id, r, v, ad} = object;

            objectType = 0;
            routeIndex = me.computeRenderer.getRouteIndex(r.id);
            if (ad && ad.color) {
                object.colorGroupIndex = me.computeRenderer.addColorGroup([{
                    id,
                    color: ad.color
                }]);
                colorIndex = me.computeRenderer.getColorIndex(id);
            } else {
                colorIndex = me.computeRenderer.getColorIndex(v ? v.id : r.id);
            }
            sectionIndex = object.sectionIndex;
            nextSectionIndex = sectionIndex + object.sectionLength;
            delay = object.delay >= configs.delayThreshold ? 1 : 0;
        } else if (object.type === 'flight') {
            const {a, feature} = object;

            objectType = 1;
            routeIndex = me.computeRenderer.getRouteIndex(feature.properties.id);
            colorIndex = me.computeRenderer.getColorIndex(a.id);
            sectionIndex = 0;
            nextSectionIndex = 1;
        } else {
            const {gtfsId, feature, offsets: stopOffsets} = object;

            objectType = 2;
            routeIndex = me.computeRenderer.getRouteIndex(`${gtfsId}.${feature.properties.id}`);
            // 그려지는 색은 점유도색, 되돌아갈 바탕은 노선색이다. 하나로 묶으면
            // 틴트를 벗길 때 점유도색으로 되돌아가 노선색을 영구히 잃는다.
            colorIndex = me.getBusColorIndex(object);
            baseColorIndex = me.getBusColorIndex(object, true);
            sectionIndex = stopOffsets[object.sectionIndex];
            nextSectionIndex = stopOffsets[object.sectionIndex + object.sectionLength];
        }

        const instanceID = object.instanceID = me.computeRenderer.addInstance(objectType, routeIndex, colorIndex, sectionIndex, nextSectionIndex, delay);

        // Kept so a repainted object can go back to the colour of its line
        object.lineColorIndex = baseColorIndex !== undefined ? baseColorIndex : colorIndex;

        me.objects.set(instanceID, object);
        Object.assign(object, me.getObjectPosition(object));
        me.needsUpdateInstances = true;
    }

    /**
     * Draws the remaining cars of a train behind its head. Only one train is drawn in
     * full at a time, which keeps the instance count flat.
     * @param {Object} object - Train to draw in full, or nothing to clear
     */
    showCars(object) {
        const me = this,
            computeRenderer = me.computeRenderer;

        me.hideCars();

        if (!object || object.type !== 'train' || !(object.carComposition > 1)) {
            return;
        }

        const {r, v, ad, sectionIndex, sectionLength} = object,
            routeIndex = computeRenderer.getRouteIndex(r.id),
            colorIndex = computeRenderer.getColorIndex(ad && ad.color ? object.id : v ? v.id : r.id),
            spacing = me.getCarSpacing(),
            instanceIDs = me.carInstanceIDs = [];

        me.carObject = object;
        object.carNumber = 1;
        for (let i = 1; i < object.carComposition; i++) {
            const instanceID = computeRenderer.addInstance(
                0, routeIndex, colorIndex, sectionIndex, sectionIndex + sectionLength,
                object.delay >= configs.delayThreshold ? 1 : 0, spacing * i
            );

            computeRenderer.copyInstanceMotion(object.instanceID, instanceID);
            instanceIDs.push(instanceID);
            // Each car is pickable in its own right, so clicking one can say which it is
            me.objects.set(instanceID, {type: 'car', train: object, carNumber: i + 1, instanceID});
        }
        me.needsUpdateInstances = true;
    }

    /**
     * Repaints an object, and the cars of a train drawn in full, with the given colour
     * entry. Pass nothing to go back to the colour of its line.
     * @param {Object} object - Object to repaint
     * @param {number} [colorIndex] - Colour entry index
     */
    setObjectColorIndex(object, colorIndex) {
        const me = this,
            index = colorIndex !== undefined ? colorIndex : object.lineColorIndex;

        if (index === undefined || object.instanceID === undefined) {
            return;
        }
        me.computeRenderer.setInstanceColorIndex(object.instanceID, index);
        if (me.carObject === object) {
            for (const instanceID of me.carInstanceIDs) {
                me.computeRenderer.setInstanceColorIndex(instanceID, index);
            }
        }
    }

    /**
     * Lays a translucent film of the given colour over an object, keeping the shape of
     * its line colours underneath. Pass nothing for the colour to take the film off.
     *
     * The colour texture has no alpha to spare (it is written as 255), so the film is
     * flattened into RGB here. All four sub-colours are covered, otherwise the accent
     * a car texture paints on the roof would stay untinted and read as a separate
     * signal. One reusable entry, so repainting never churns colour groups.
     *
     * @param {Object} object - Object to cover
     * @param {string} [color] - Film colour
     * @param {number} [alpha] - Film opacity, 0 to 1
     */
    setObjectTint(object, color, alpha) {
        const me = this,
            computeRenderer = me.computeRenderer;

        // 덮을 대상이 없는 건 정상 상태다 (아무것도 선택하지 않았을 때). 여기서 막지
        // 않으면 클록 틱마다 던져 애니메이션 루프가 죽고 열차가 아예 안 나온다.
        if (!object || object.instanceID === undefined || object.lineColorIndex === undefined) {
            return;
        }

        const {lineColorIndex} = object;
        if (color === undefined) {
            me.setObjectColorIndex(object);
            return;
        }
        if (me.tintColorIndex === undefined) {
            computeRenderer.addColorGroup([{id: TINT_COLOR_ID, color}]);
            me.tintColorIndex = computeRenderer.getColorIndex(TINT_COLOR_ID);
        }

        // 바탕은 항상 lineColorIndex 에서 읽는다. 현재 색에서 읽으면 이미 덮인 색 위에
        // 또 덮어 매 갱신마다 오버레이가 짙어진다.
        const overlay = colorToRGBArray(color),
            base = computeRenderer.getColorsByIndex(lineColorIndex);

        // 서브컬러마다 다르게 덮는다. 차체는 진하게 덮어야 혼잡도가 읽히고, 노선색
        // 띠는 옅게 덮어야 어느 노선인지 남는다. 하나로 묶으면 둘 중 하나를 잃는다.
        const alphas = Array.isArray(alpha) ? alpha : [alpha, alpha, alpha, alpha];

        computeRenderer.setColorsByIndex(me.tintColorIndex, base.map((rgb, i) => blendRGB(rgb, overlay, alphas[i])));
        me.setObjectColorIndex(object, me.tintColorIndex);
    }

    hideCars() {
        const me = this,
            instanceIDs = me.carInstanceIDs;

        if (!instanceIDs) {
            return;
        }
        delete me.carObject.carNumber;
        delete me.carObject.carInstanceID;
        for (const instanceID of instanceIDs) {
            me.objects.delete(instanceID);
            me.computeRenderer.removeInstance(instanceID);
        }
        delete me.carInstanceIDs;
        delete me.carObject;
        me.needsUpdateInstances = true;
    }

    updateObject(object, timeOffset, duration, accelerationTime, normalizedAcceleration, decelerationTime, normalizedDeceleration) {
        const me = this;
        let sectionIndex, nextSectionIndex;

        if (object.type === 'train') {
            sectionIndex = object.sectionIndex;
            nextSectionIndex = sectionIndex + object.sectionLength;
        } else if (object.type === 'flight') {
            sectionIndex = 0;
            nextSectionIndex = 1;
        } else {
            const stopOffsets = object.offsets;

            sectionIndex = stopOffsets[object.sectionIndex];
            nextSectionIndex = stopOffsets[object.sectionIndex + object.sectionLength];
        }

        me.computeRenderer.updateInstance(object.instanceID, sectionIndex, nextSectionIndex, timeOffset, duration, accelerationTime, normalizedAcceleration, decelerationTime, normalizedDeceleration);
        if (me.carObject === object) {
            for (const instanceID of me.carInstanceIDs) {
                me.computeRenderer.copyInstanceMotion(object.instanceID, instanceID);
            }
        }
        Object.assign(object, me.getObjectPosition(object));
        me.needsUpdateInstances = true;
    }

    getObjectPosition(object) {
        return this.computeRenderer.getInstancePosition(object.instanceID);
    }

    /**
     * Returns the instance the user actually pointed at: the car for a train drawn in
     * full, the object itself otherwise.
     * @param {Object} object - Picked object
     * @returns {number} Instance ID
     */
    getPickedInstanceID(object) {
        return object.carInstanceID !== undefined ? object.carInstanceID : object.instanceID;
    }

    /**
     * Returns where the picked car sits, so the outline and the popup land on the car
     * the user pointed at instead of the head of the train.
     * @param {Object} object - Picked object
     * @returns {Object} Object with coord, altitude, bearing and _t
     */
    getPickedPosition(object) {
        return this.computeRenderer.getInstancePosition(this.getPickedInstanceID(object));
    }

    /**
     * Returns where a given instance currently sits.
     * @param {number} instanceID - Instance ID
     * @returns {Object} Object with coord, altitude, bearing and _t
     */
    getInstancePosition(instanceID) {
        return this.computeRenderer.getInstancePosition(instanceID);
    }

    /**
     * Returns where a car of the fully drawn train currently sits.
     * @param {number} index - Car index, 0 being the car right behind the head
     * @returns {Object} Object with coord, altitude, bearing and _t
     */
    getCarPosition(index) {
        return this.computeRenderer.getInstancePosition(this.carInstanceIDs[index]);
    }

    /**
     * Returns the gap between the centres of two cars. The car meshes are exaggerated
     * as the map zooms out (mesh-set/common-vertex.glsl), so the gap follows the very
     * same curve - that is what keeps the train looking equally coupled at every zoom.
     * @returns {number} Distance between car centres, in route distance units
     */
    getCarSpacing() {
        // 열차 전용이다 (버스는 단일 인스턴스). 셰이더의 비-BUS 상한과 같아야 한다.
        const zoom = Math.min(Math.max(this.map.getZoom(), 13), 19);

        return CAR_MESH_LENGTH_KM * Math.pow(2, 14 - zoom) * CAR_GAP_RATIO;
    }

    removeObject(object) {
        const me = this,
            {instanceID, colorGroupIndex} = object;

        if (instanceID !== undefined) {
            me.objects.delete(instanceID);
            delete object.instanceID;
            return me.computeRenderer.removeInstance(instanceID).then(() => {
                if (colorGroupIndex !== undefined) {
                    me.computeRenderer.removeColorGroup(colorGroupIndex);
                    delete object.colorGroupIndex;
                }
                me.needsUpdateInstances = true;
            });
        }
        return Promise.resolve();
    }

    pickObject(mode, point) {
        const me = this,
            {pickingTexture, pixelBuffer} = me,
            {renderer, camera} = me.context,
            rendererContext = renderer.getContext(),
            pixelRatio = window.devicePixelRatio,
            scene = mode === 'underground' ? me.ugPickingScene : me.ogPickingScene;

        camera.setViewOffset(
            rendererContext.drawingBufferWidth,
            rendererContext.drawingBufferHeight,
            point.x * pixelRatio | 0,
            point.y * pixelRatio | 0,
            1,
            1
        );

        renderer.resetState();
        renderer.setRenderTarget(pickingTexture);
        renderer.render(scene, camera);
        renderer.setRenderTarget(null);
        renderer.resetState();

        camera.clearViewOffset();

        renderer.readRenderTargetPixels(pickingTexture, 0, 0, 1, 1, pixelBuffer);

        return me.objects.get((pixelBuffer[0] << 16) | (pixelBuffer[1] << 8) | pixelBuffer[2]);
    }

    refreshDelayMarkers() {
        const me = this,
            dark = hasDarkBackground(me.map.map);

        me.ugCarMeshSet.refreshDelayMarkerMesh(dark);
        me.ogCarMeshSet.refreshDelayMarkerMesh(dark);
    }

    setTimeOffset(timeOffset) {
        this.computeRenderer.setTimeOffset(timeOffset);
    }

    markObject(object) {
        const me = this,
            // 마우스를 올린 것만으로는 윤곽선이 칸 사이를 옮겨다니지 않게 한다.
            // 칸을 고르는 것은 클릭이고(trackObject 가 getPickedInstanceID 를 쓴다),
            // hover 는 어느 열차인지만 가리킨다. 펼치지 않은 열차는 칸이 하나뿐이라
            // instanceID 가 곧 그 칸이므로 평소 화면에는 변화가 없다.
            instanceID = object ? object.instanceID : -1;

        me.computeRenderer.setMarked(instanceID);
        me.ugCarMeshSet.setMarkedInstanceID(instanceID);
        me.ogCarMeshSet.setMarkedInstanceID(instanceID);
        me.aircraftMeshSet.setMarkedInstanceID(instanceID);
        me.busMeshSet.setMarkedInstanceID(instanceID);
        me.needsUpdateInstances = true;
    }

    trackObject(object) {
        const me = this,
            // 사용자가 고른 칸에 윤곽선을 그린다. markObject 와 같은 기준이어야
            // 마우스를 올렸을 때와 클릭했을 때 윤곽선이 다른 칸에 그려지지 않는다.
            instanceID = object ? me.getPickedInstanceID(object) : -1;

        me.computeRenderer.setTracked(instanceID);
        me.ugCarMeshSet.setTrackedInstanceID(instanceID);
        me.ogCarMeshSet.setTrackedInstanceID(instanceID);
        me.aircraftMeshSet.setTrackedInstanceID(instanceID);
        me.busMeshSet.setTrackedInstanceID(instanceID);
        me.needsUpdateInstances = true;
    }

    project(lnglat, altitude) {
        const {map, context} = this,
            {width, height} = map.map.transform,
            {x, y, z} = map.getModelPosition(lnglat, altitude),
            {x: px, y: py} = new Vector3(x, y, z).project(context.camera);

        return new Point((px + 1) / 2 * width, (1 - py) / 2 * height);
    }

}
